import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Auth/firebase";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AssigneeSelector from "./AssigneeSelector";
import ClientSelector from "./ClientSelector"; // Import the new component

const EditProject = () => {
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(new Date());
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [clients, setClients] = useState([]); // Add state for clients
  const [selectedClients, setSelectedClients] = useState([]); // State for selected clients

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchProject = async () => {
      const docRef = doc(db, "projects", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const project = docSnap.data();
        setProjectName(project.name);
        setDescription(project.description);
        const parsedDueDate = new Date(project.dueDate);
        setDueDate(isNaN(parsedDueDate.getTime()) ? new Date() : parsedDueDate);
        setSelectedCategory(project.category);
        setSelectedAssignees(project.assignees || []);
        setSelectedClients(project.clients || []); // Set selected clients
      }
    };

    fetchProject();
  }, [id]);

  useEffect(() => {
    const fetchCategories = async () => {
      const categoryCollection = collection(db, "categories");
      const categorySnapshot = await getDocs(categoryCollection);
      const categoriesList = categorySnapshot.docs.map(
        (doc) => doc.data().name
      );
      setCategories(categoriesList);
    };

    const fetchAssigneesAndClients = async () => {
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAssignees(usersList); // Use the same users list for assignees and clients
    };

    fetchCategories();
    fetchAssigneesAndClients();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const project = {
      name: projectName,
      description,
      dueDate,
      category: selectedCategory,
      assignees: selectedAssignees,
      clients: selectedClients, // Add clients to the project document
    };

    const docRef = doc(db, "projects", id);
    await updateDoc(docRef, project);

    navigate("/projects");
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded-lg w-full max-w-lg space-y-6"
      >
        <h2 className="text-xl font-semibold text-center">Edit Project</h2>
        <div>
          <label className="block mb-2 text-sm font-medium">Project Name</label>
          <input
            type="text"
            placeholder="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Description</label>
          <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Due Date</label>
          <DatePicker
            selected={dueDate}
            onChange={(date) => setDueDate(date)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
          >
            <option value="">Select a Category</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Assignees</label>
          <AssigneeSelector
            assignees={assignees}
            selectedAssignees={selectedAssignees}
            setSelectedAssignees={setSelectedAssignees}
          />
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">Clients</label>
          <ClientSelector
            users={assignees} // Use the same users list from assignees
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
          />
        </div>
        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 rounded-lg"
        >
          Update Project
        </button>
        <button
          type="button"
          className="w-full bg-gray-500 text-white py-2 rounded-lg mt-2"
          onClick={() => navigate("/projects")}
        >
          Cancel
        </button>
      </form>
    </div>
  );
};

export default EditProject;
